import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { authApi } from '@/api/auth.api';
import { riderApi } from '@/api/rider.api';
import type { AuthUser } from '@/api/types';
import { setSessionExpiredHandler } from '@/lib/api-client';
import { disablePush } from '@/lib/push';
import { queryKeys } from '@/lib/query-keys';
import { tokenStore } from '@/lib/token-store';

export type AuthStatus = 'restoring' | 'signedOut' | 'signedIn';

type AuthValue = {
  status: AuthStatus;
  user: AuthUser | null;
  isSignedIn: boolean;
  requestOtp: (phone: string) => Promise<string | null>;
  verifyOtp: (input: { phone: string; code: string }) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Signed in or not, and one moment in between.
 *
 * Unlike the customer app, being signed out here IS a wall. There is nothing
 * to browse — this app is one list of jobs addressed to one person, and every
 * route behind the sign-in screen is refused by the backend to an anonymous
 * caller anyway.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [user, setUser] = useState<AuthUser | null>(null);
  const queryClient = useQueryClient();

  const forceSignOut = useCallback(() => {
    // The session died underneath us. No logout call — there is nothing to
    // revoke, and the token that would authorise it is the one that failed.
    tokenStore.clear();
    queryClient.clear();
    setUser(null);
    setStatus('signedOut');
  }, [queryClient]);

  useEffect(() => {
    setSessionExpiredHandler(forceSignOut);
    return () => setSessionExpiredHandler(null);
  }, [forceSignOut]);

  // Cold start.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!tokenStore.getRefreshToken()) {
        if (!cancelled) setStatus('signedOut');
        return;
      }
      /*
       * There is no access token in memory after a reload, only a refresh
       * token in storage. Rather than mint one eagerly, /auth/me is allowed to
       * 401 and the client's refresh path recovers it — one code path for
       * expiry and for cold start, so there is no second implementation to
       * keep in step.
       *
       * This matters more here than in the customer app: a rider opens this at
       * the start of a shift, having last used it a day ago, so the cold-start
       * path is the normal one rather than the exception.
       */
      try {
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me);
        setStatus('signedIn');
      } catch {
        if (cancelled) return;
        tokenStore.clear();
        setStatus('signedOut');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // A sign-out in another tab must not leave this one holding a live session.
  useEffect(
    () =>
      tokenStore.subscribe(() => {
        if (!tokenStore.getRefreshToken() && !tokenStore.getAccessToken()) {
          setUser(null);
          setStatus('signedOut');
        }
      }),
    [],
  );

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      isSignedIn: status === 'signedIn',

      requestOtp: (phone) => authApi.requestOtp(phone),

      verifyOtp: async (input) => {
        const result = await authApi.verifyOtp(input);
        tokenStore.set({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setUser(result.user);
        setStatus('signedIn');
        // Anything cached before this was cached for nobody in particular.
        queryClient.invalidateQueries();
      },

      /**
       * Signs out, after taking the rider off shift.
       *
       * Going offline first is the point of the whole method: a rider who
       * closes the app while still `AVAILABLE` keeps being handed jobs nobody
       * is going to pick up, and the only sign anything is wrong is a delivery
       * ageing on a dispatch board they cannot see.
       *
       * Every step is best effort. A failed round trip must not trap someone
       * on a screen they asked to leave.
       */
      signOut: async () => {
        try {
          const rider = queryClient.getQueryData<{ id: string; status: string } | null>(
            queryKeys.riderProfile,
          );
          if (rider && (rider.status === 'AVAILABLE' || rider.status === 'BUSY')) {
            await riderApi.setOnShift(rider.id, false);
          }
        } catch {
          /* still signing out */
        }

        // Before the token goes: the server needs to know whose row to drop,
        // or a shared phone keeps buzzing with the previous rider's jobs.
        await disablePush();

        const refreshToken = tokenStore.getRefreshToken();
        if (refreshToken) {
          try {
            await authApi.logout(refreshToken);
          } catch {
            /* revoked locally regardless */
          }
        }

        tokenStore.clear();
        queryClient.clear();
        setUser(null);
        setStatus('signedOut');
      },
    }),
    [status, user, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
